import type { OptimisticTaskFields } from "@api/task.api";
import { TaskStatus, type Task, type TaskGroupBy } from "@common/types/task";
import type {
    BoardMove,
    BoardMoveCheck,
    BoardMoveVerdict,
} from "@design-system/Board/Board.types";
import {
    getTaskAxisChange,
    type TaskAxisChange,
    type TaskBoardMoveBody,
} from "./taskBoardAxisChanges";
import type { TaskBoardLaneBy } from "./taskBoardLanes";
import {
    findTask,
    getTaskPlaceValues,
    type TaskPlaceValues,
} from "./taskBoardPlace";
import type { TaskBoardSort } from "./taskBoardSort";

export type { TaskBoardMoveBody } from "./taskBoardAxisChanges";

/** What a drop sends, and what the cache shows until the response lands. */
export interface TaskBoardMoveUpdate {
    body: TaskBoardMoveBody;
    patch: TaskBoardMoveBody & OptimisticTaskFields;
}

/** What the board's columns and lanes show, which decides what a drop sets. */
export interface TaskBoardAxes {
    groupBy: TaskGroupBy;
    laneBy: TaskBoardLaneBy | null;
    /** Each entity's client, so an entity lane refuses another client's task. */
    entityWorkspaceIds: ReadonlyMap<number, string>;
    /** The organization page, where a task never moves between clients. */
    isCrossWorkspace: boolean;
}

type TaskMoveEnds = Pick<BoardMoveCheck<Task>, "item" | "from" | "to">;

const isRefusal = (change: TaskAxisChange): change is { refusal: string } =>
    "refusal" in change;

/** The change on the column axis and, with lanes, on the lane axis. */
const getMoveChanges = (
    { item, from, to }: TaskMoveEnds,
    { groupBy, laneBy, entityWorkspaceIds, isCrossWorkspace }: TaskBoardAxes,
): TaskAxisChange[] => [
    getTaskAxisChange({
        task: item,
        axis: groupBy,
        fromId: from.columnId,
        toId: to.columnId,
        entityWorkspaceIds,
        isCrossWorkspace,
    }),
    laneBy === null || from.laneId === null || to.laneId === null
        ? { body: {} }
        : getTaskAxisChange({
              task: item,
              axis: laneBy,
              fromId: from.laneId,
              toId: to.laneId,
              entityWorkspaceIds,
              isCrossWorkspace,
          }),
];

/** Both axes' fields, or null when either refuses the drop. */
const getMoveBody = (
    ends: TaskMoveEnds,
    axes: TaskBoardAxes,
): TaskBoardMoveBody | null => {
    let body: TaskBoardMoveBody = {};
    for (const change of getMoveChanges(ends, axes)) {
        if (isRefusal(change)) {
            return null;
        }
        body = { ...body, ...change.body };
    }
    return body;
};

/** The `canMove` for the board's axes: refuses cells no single update stands for. */
export const createTaskMoveRule =
    (axes: TaskBoardAxes) =>
    (ends: TaskMoveEnds): BoardMoveVerdict => {
        const refusal = getMoveChanges(ends, axes).find(isRefusal);
        return refusal === undefined
            ? { isAllowed: true }
            : { isAllowed: false, reason: refusal.refusal };
    };

/** A person as a task's `lastDoneBy` names them. */
export type TaskCompleter = NonNullable<Task["lastDoneBy"]>;

/** A column or lane value: an id and the plain-text title it shows. */
interface TaskAxisValue {
    id: string;
    title: string;
}

interface TaskBoardMoveArgs {
    move: BoardMove<Task>;
    axes: TaskBoardAxes;
    sort: TaskBoardSort;
    /** The board's tasks, to find the drop's neighbours. */
    tasks: readonly Task[];
    /** The columns and lanes, to name a new assignee. */
    axisValues: readonly TaskAxisValue[];
    /** The user dropping, whom the server stamps as finishing a task moved into Done. */
    completer: TaskCompleter | null;
}

/**
 * The assignee the cache shows for a drop under By assignee: a copy from
 * another of their tasks, else one named after the column or lane, until
 * the response brings the real one.
 */
const findAssignee = ({
    assigneeId,
    tasks,
    axisValues,
}: Pick<TaskBoardMoveArgs, "tasks" | "axisValues"> & {
    assigneeId: number;
}): NonNullable<Task["assignee"]> => {
    const assignee = tasks.find(
        (task) => task.assignee?.id === assigneeId,
    )?.assignee;
    if (assignee) {
        return assignee;
    }
    const name =
        axisValues.find((value) => value.id === String(assigneeId))?.title ??
        "";
    return {
        id: assigneeId,
        name,
        firstName: name,
        lastName: "",
        email: null,
        avatar: null,
        iconColor: "",
    };
};

/** The copied values that differ from the task's own. */
const getChangedPlaceValues = (
    task: Task,
    { dueDate, priority }: TaskPlaceValues,
): TaskPlaceValues => ({
    ...(dueDate !== undefined && dueDate !== task.dueDate && { dueDate }),
    ...(priority !== undefined && priority !== task.priority && { priority }),
});

/** When and by whom the task was last done once `status` saves, as the server stamps it. */
const getLastDone = ({
    task,
    status,
    now,
    completer,
}: Pick<TaskBoardMoveArgs, "completer"> & {
    task: Task;
    status: TaskStatus | undefined;
    now: Date;
}): Pick<Task, "lastDoneAt" | "lastDoneById" | "lastDoneBy"> => {
    if (status !== undefined && status !== task.status) {
        if (status === TaskStatus.DONE) {
            return {
                lastDoneAt: now,
                lastDoneById: completer?.id ?? null,
                lastDoneBy: completer,
            };
        }
        if (task.status === TaskStatus.DONE) {
            return { lastDoneAt: null, lastDoneById: null, lastDoneBy: null };
        }
    }
    return {
        lastDoneAt: task.lastDoneAt,
        lastDoneById: task.lastDoneById,
        lastDoneBy: task.lastDoneBy,
    };
};

/**
 * What a drop sends: the values of the column and lane it landed in, and
 * with the place key held, the sort field's value of the neighbour it was
 * placed beside. Null when nothing changes or the cell takes no drops. The
 * cache also gets the times and completer the server stamps, so the card
 * keeps its spot under a sort by them.
 */
export const getTaskBoardMoveUpdate = ({
    move,
    axes,
    sort,
    tasks,
    axisValues,
    completer,
}: TaskBoardMoveArgs): TaskBoardMoveUpdate | null => {
    const task = move.item;
    const moveBody = getMoveBody(move, axes);
    if (moveBody === null) {
        return null;
    }
    const placeValues = move.isPlacedInSort
        ? getTaskPlaceValues({
              sort,
              axes,
              before: findTask(tasks, move.beforeItemId),
              after: findTask(tasks, move.afterItemId),
          })
        : {};
    const body: TaskBoardMoveBody = {
        ...getChangedPlaceValues(task, placeValues),
        ...moveBody,
    };
    if (Object.keys(body).length === 0) {
        return null;
    }
    const now = new Date();
    const stamped = {
        ...body,
        updatedAt: now,
        ...getLastDone({ task, status: body.status, now, completer }),
    };
    const { assigneeId } = body;
    if (assigneeId === undefined) {
        return { body, patch: stamped };
    }
    return {
        body,
        patch: {
            ...stamped,
            assignee:
                assigneeId === null
                    ? null
                    : findAssignee({ assigneeId, tasks, axisValues }),
        },
    };
};

/** The board's `getMovedItem`: the task as a drop leaves it in the cache. */
export const getMovedTask = <TTask extends Task>({
    move,
    ...args
}: Omit<TaskBoardMoveArgs, "move"> & {
    move: BoardMoveCheck<TTask>;
}): TTask => {
    const update = getTaskBoardMoveUpdate({
        ...args,
        move: {
            ...move,
            beforeItemId: null,
            afterItemId: null,
            isPlacedInSort: false,
        },
    });
    return update === null ? move.item : { ...move.item, ...update.patch };
};
